import { useState, useEffect } from 'react'
import { 
  ArrowLeft, 
  Edit, 
  Play, 
  Pause, 
  Calendar,
  Clock,
  Target,
  TrendingUp,
  Settings,
  Download,
  Copy,
  CheckCircle,
  Circle,
  AlertCircle
} from 'lucide-react'
import { useProgramStore } from '@/stores/programStore'
import { ProgramService } from '@/services'
import { Program, ProgramCalendarEvent } from '@/types'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { ProgramCalendarView } from './ProgramCalendarView'
import { format, addDays } from 'date-fns'
import toast from 'react-hot-toast'

interface ProgramDetailViewProps {
  programId: string
  onBack: () => void
  onEdit: () => void
}

export function ProgramDetailView({ programId, onBack, onEdit }: ProgramDetailViewProps) {
  const {
    currentProgram,
    setCurrentProgram,
    setCalendarEvents,
    updateProgram
  } = useProgramStore()

  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<'overview' | 'workouts' | 'calendar' | 'analytics'>('overview')
  const [selectedWeek, setSelectedWeek] = useState(1)

  useEffect(() => {
    loadProgram()
  }, [programId])

  // Transform fit-engine data to expected format
  const transformProgramData = async (rawProgram: any) => {
    // Handle nested program structure from different API responses
    console.log('🔍 Raw API response:', rawProgram)
    const programData = rawProgram.data?.program || rawProgram.program || rawProgram
    
    console.log('🔍 Extracted program data:', programData)
    console.log('🔍 Has workouts?', !!programData.workouts, 'Is array?', Array.isArray(programData.workouts))
    
    // NEW: Check if we have the new workout structure from the database
    if (programData.workouts && Array.isArray(programData.workouts)) {
      console.log('✅ Using new workout structure from database')
      console.log('🏋️ Database workouts:', programData.workouts)
      
      // Transform database workouts into the expected weeks structure
      const weeks: any[] = []
      const workoutsByWeek = new Map<number, any[]>()
      
      // Group workouts by week
      programData.workouts.forEach((workout: any) => {
        console.log(`🏋️ Processing workout: ${workout.name}, Week: ${workout.week}, Day: ${workout.day}, AssignedDate: ${workout.assignedDate}`)
        if (!workoutsByWeek.has(workout.week)) {
          workoutsByWeek.set(workout.week, [])
        }
        
        // Transform workout exercises into the expected format
        const exercises = workout.exercises?.map((workoutEx: any) => ({
          id: workoutEx.exercise.id,
          name: workoutEx.exercise.name || 'Unknown Exercise',
          sets: workoutEx.sets || 0,
          reps: workoutEx.reps || 'N/A',
          weight: workoutEx.weight || null,
          duration: workoutEx.duration || null,
          restTime: workoutEx.restTime || null,
          muscleGroups: workoutEx.exercise.muscleGroups || []
        })) || []
        
        workoutsByWeek.get(workout.week)!.push({
          id: workout.id,
          name: workout.name,
          day: workout.day - 1, // Convert from 1-based to 0-based for calendar
          assignedDate: workout.assignedDate, // Store the assigned date
          completed: workout.isCompleted,
          estimatedDuration: workout.estimatedDuration || exercises.length * 8,
          difficulty: workout.difficulty || 5,
          mainWork: exercises,
          description: workout.description || workout.notes
        })
      })
      
      // Convert grouped workouts to weeks array
      const maxWeek = Math.max(...Array.from(workoutsByWeek.keys()))
      for (let weekNum = 1; weekNum <= maxWeek; weekNum++) {
        const weekWorkouts = workoutsByWeek.get(weekNum) || []
        weeks.push({
          weekNumber: weekNum,
          workouts: weekWorkouts,
          deloadWeek: false // Could be determined from workout names/descriptions
        })
      }
      
      // Calculate workouts per week
      const workoutsPerWeek = weeks.length > 0 ? 
        Math.round(weeks.reduce((sum, week) => sum + week.workouts.length, 0) / weeks.length) : 0
      
      // Format equipment list
      const availableEquipment = programData.metadata?.generationRequest?.availableEquipment || []
      const equipmentNames = availableEquipment.length > 0 ? availableEquipment : ['Barbell', 'Dumbbells', 'Bench']
      
      // Set default start date if not provided
      const startDate = programData.startDate || new Date().toISOString()
      
      const result = {
        ...programData,
        duration: programData.totalWeeks || weeks.length || 8,
        workoutsPerWeek,
        focus: programData.goals?.map((goal: string) => ({ type: goal })) || [{ type: 'general' }],
        difficulty: programData.metadata?.generationRequest?.experienceLevel || 'intermediate',
        equipment: equipmentNames,
        weeks,
        startDate,
        progress: { completionRate: 0 }
      }
      
      console.log('✅ Transformed program with new workout structure:', result)
      return result
    }
    
    // FALLBACK: Old transformation for backward compatibility with fit-engine response
    console.log('⚠️ Falling back to old fit-engine transformation')
    
    // Check different possible paths for fit engine data
    let fitEngineData = programData.metadata?.fitEngineResponse?.program
    
    if (!fitEngineData && programData.metadata?.fitEngineResponse) {
      fitEngineData = programData.metadata.fitEngineResponse.program
    }
    
    if (!fitEngineData) {
      console.log('⚠️ No fit engine data found, returning raw program')
      return programData
    }

    // Calculate workouts per week from schedule
    let workoutsPerWeek = 0
    if (fitEngineData.mesocycles?.[0]?.phases?.[0]?.weeks?.[0]?.schedule) {
      const schedule = fitEngineData.mesocycles[0].phases[0].weeks[0].schedule
      workoutsPerWeek = Object.values(schedule).flat().length
    }

    // Create exercise map with fallback names (since UUIDs don't work with current API)
    const exerciseMap = new Map<string, string>()
    const exerciseNames = [
      'Barbell Bench Press', 'Incline Dumbbell Press', 'Barbell Squat', 'Romanian Deadlift',
      'Overhead Press', 'Barbell Row', 'Pull-ups', 'Dips', 'Lateral Raises', 'Tricep Extensions',
      'Bicep Curls', 'Leg Press', 'Leg Curls', 'Calf Raises', 'Face Pulls', 'Chest Fly'
    ]
    
    let nameIndex = 0
    fitEngineData.mesocycles?.forEach((mesocycle: any) => {
      mesocycle.phases?.forEach((phase: any) => {
        phase.weeks?.forEach((week: any) => {
          Object.values(week.schedule).forEach((dayWorkouts: any) => {
            if (Array.isArray(dayWorkouts)) {
              dayWorkouts.forEach((workout: any) => {
                workout.assigned_exercises?.forEach((ex: any) => {
                  if (ex.exercise_id && !exerciseMap.has(ex.exercise_id)) {
                    exerciseMap.set(ex.exercise_id, exerciseNames[nameIndex % exerciseNames.length])
                    nameIndex++
                  }
                })
              })
            }
          })
        })
      })
    })

    // Transform weeks data
    const weeks: any[] = []
    let weekCounter = 1

    // Day name to number mapping for calendar
    const dayNameToNumber: Record<string, number> = {
      sunday: 0, monday: 1, tuesday: 2, wednesday: 3,
      thursday: 4, friday: 5, saturday: 6
    }

    fitEngineData.mesocycles?.forEach((mesocycle: any) => {
      mesocycle.phases?.forEach((phase: any) => {
        phase.weeks?.forEach((week: any) => {
          const weekWorkouts: any[] = []
          
          // Extract workouts from schedule
          Object.entries(week.schedule).forEach(([day, dayWorkouts]: [string, any]) => {
            if (Array.isArray(dayWorkouts)) {
              dayWorkouts.forEach((workout: any, index: number) => {
                const exercises = workout.assigned_exercises?.map((ex: any) => ({
                  id: ex.exercise_id,
                  name: exerciseMap.get(ex.exercise_id) || `Exercise ${ex.exercise_id.substring(0, 8)}...`,
                  sets: ex.sets || [],
                  reps: ex.sets?.[0]?.rep_range_min || ex.sets?.[0]?.reps || null,
                  weight: ex.sets?.[0]?.target_percentage_1RM || ex.sets?.[0]?.weight || null,
                  muscleGroups: []
                })) || []

                weekWorkouts.push({
                  id: `${week.week_idx}-${day}-${index}`,
                  name: workout.name || `${day.charAt(0).toUpperCase() + day.slice(1)} Workout`,
                  day: dayNameToNumber[day.toLowerCase()] ?? 1, // Convert day name to number
                  completed: false,
                  estimatedDuration: exercises.length * 8, // ~8 minutes per exercise
                  difficulty: 7,
                  mainWork: exercises,
                  description: phase.instructions
                })
              })
            }
          })

          weeks.push({
            weekNumber: weekCounter++,
            workouts: weekWorkouts,
            deloadWeek: phase.title?.toLowerCase().includes('deload') || false
          })
        })
      })
    })

    // Format equipment list
    const availableEquipment = programData.metadata?.generationRequest?.availableEquipment || []
    const equipmentNames = availableEquipment.length > 0 ? availableEquipment : ['Barbell', 'Dumbbells', 'Bench']

    // Set default start date if not provided (needed for calendar generation)
    const startDate = programData.startDate || new Date().toISOString()

    const result = {
      ...programData,
      duration: programData.totalWeeks || 8,
      workoutsPerWeek,
      focus: programData.goals?.map((goal: string) => ({ type: goal })) || [{ type: 'general' }],
      difficulty: programData.metadata?.generationRequest?.experienceLevel || 'intermediate',
      equipment: equipmentNames,
      weeks,
      startDate,
      progress: { completionRate: 0 }
    }

    return result
  }

  const loadProgram = async () => {
    setLoading(true)
    try {
      const rawProgram = await ProgramService.getProgramById(programId)
      console.log('🔍 Raw program response:', rawProgram)
      
      // Also try the dedicated workouts endpoint for comparison
      try {
        const { apiService } = await import('@/services/api')
        const workoutsResponse = await apiService.getProgramWorkouts(programId)
        console.log('🏋️ Dedicated workouts endpoint response:', workoutsResponse)
      } catch (workoutsError) {
        console.log('⚠️ Dedicated workouts endpoint failed:', workoutsError)
      }
      
      const transformedProgram = await transformProgramData(rawProgram)
      setCurrentProgram(transformedProgram)
      
      // Generate calendar events for the program
      generateCalendarEvents(transformedProgram)
    } catch (error) {
      console.error('Failed to load program:', error)
      toast.error('Failed to load program details')
    } finally {
      setLoading(false)
    }
  }

  const generateCalendarEvents = (program: Program) => {
    if (!program.weeks) return

    const events: ProgramCalendarEvent[] = []
    const startDate = new Date(program.startDate || new Date())

    program.weeks.forEach((week, weekIndex) => {
      const weekStartDate = addDays(startDate, weekIndex * 7)
      
      week.workouts.forEach((workout) => {
        // Use assigned date if available, otherwise calculate from program start date
        let workoutDate: Date
        
        if (workout.assignedDate) {
          // Use the explicitly assigned date
          workoutDate = new Date(workout.assignedDate)
          console.log(`📅 Using assigned date for ${workout.name}: ${workoutDate.toDateString()}`)
        } else {
          // Fall back to calculated date based on program structure
          workoutDate = addDays(weekStartDate, workout.day)
          console.log(`📅 Using calculated date for ${workout.name}: ${workoutDate.toDateString()}`)
        }
        
        const endDate = new Date(workoutDate)
        endDate.setHours(endDate.getHours() + Math.floor(workout.estimatedDuration / 60))
        endDate.setMinutes(endDate.getMinutes() + (workout.estimatedDuration % 60))

        const calendarEvent = {
          id: `${program.id}-${weekIndex}-${workout.id}`,
          title: workout.name,
          start: workoutDate,
          end: endDate,
          type: 'workout' as const,
          workoutId: workout.id,
          clientId: program.clientId,
          status: workout.completed ? ('completed' as const) : ('scheduled' as const)
        }
        
        console.log(`📅 Creating calendar event: ${workout.name} on ${workoutDate.toDateString()}`)
        events.push(calendarEvent)
      })
    })

    console.log(`📅 Generated ${events.length} calendar events`)
    setCalendarEvents(events)
  }

  const handleStatusChange = async (newStatus: Program['status']) => {
    if (!currentProgram) return

    try {
      await updateProgram(programId, { status: newStatus })
      setCurrentProgram({ ...currentProgram, status: newStatus })
      toast.success(`Program ${newStatus}`)
    } catch (error) {
      console.error('Failed to update program status:', error)
      toast.error('Failed to update program status')
    }
  }

  const handleDuplicateProgram = async () => {
    try {
      // const duplicated = await ProgramService.duplicateProgram(programId)
      toast.success('Program duplicated successfully')
    } catch (error) {
      console.error('Failed to duplicate program:', error)
      toast.error('Failed to duplicate program')
    }
  }

  const handleExportProgram = async (format: 'pdf' | 'json' | 'csv') => {
    try {
      const blob = await ProgramService.exportProgram(programId, format)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${currentProgram?.name || 'program'}.${format}`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      toast.success(`Program exported as ${format.toUpperCase()}`)
    } catch (error) {
      console.error('Failed to export program:', error)
      toast.error('Failed to export program')
    }
  }

  const getStatusBadge = (status?: Program['status']) => {
    if (!status) {
      return (
        <span className={`px-3 py-1 text-sm font-medium rounded-full bg-gray-100 text-gray-700`}>
          Draft
        </span>
      )
    }

    const statusStyles: Record<string, string> = {
      draft: 'bg-gray-100 text-gray-700',
      active: 'bg-green-100 text-green-700',
      completed: 'bg-blue-100 text-blue-700',
      paused: 'bg-yellow-100 text-yellow-700'
    }

    const style = statusStyles[status] || statusStyles['draft']
    const label = (status.charAt(0).toUpperCase() + status.slice(1)) as string

    return (
      <span className={`px-3 py-1 text-sm font-medium rounded-full ${style}`}>
        {label}
      </span>
    )
  }

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!currentProgram) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Program not found</h3>
        <p className="text-gray-600 mb-4">The requested program could not be loaded.</p>
        <button onClick={onBack} className="coach-button-primary px-4 py-2">
          Back to Programs
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="p-2 text-gray-400 hover:text-gray-600"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h1 className="text-2xl font-bold text-gray-900">{currentProgram?.name || 'Program'}</h1>
              {getStatusBadge(currentProgram?.status)}
            </div>
            {currentProgram?.description && (
              <p className="text-gray-600">{currentProgram.description}</p>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <div className="relative group">
            <button className="p-2 text-gray-400 hover:text-gray-600">
              <Settings className="w-5 h-5" />
            </button>
            <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
              <div className="py-1">
                <button
                  onClick={handleDuplicateProgram}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Duplicate Program
                </button>
                <button
                  onClick={() => handleExportProgram('pdf')}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export as PDF
                </button>
                <button
                  onClick={() => handleExportProgram('json')}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export as JSON
                </button>
              </div>
            </div>
          </div>
          
          {currentProgram.status === 'draft' && (
            <button
              onClick={() => handleStatusChange('active')}
              className="coach-button-secondary px-4 py-2 flex items-center"
            >
              <Play className="w-4 h-4 mr-2" />
              Activate
            </button>
          )}
          
          {currentProgram.status === 'active' && (
            <button
              onClick={() => handleStatusChange('paused')}
              className="coach-button-secondary px-4 py-2 flex items-center"
            >
              <Pause className="w-4 h-4 mr-2" />
              Pause
            </button>
          )}
          
          <button
            onClick={onEdit}
            className="coach-button-primary px-4 py-2 flex items-center"
          >
            <Edit className="w-4 h-4 mr-2" />
            Edit
          </button>
        </div>
      </div>

      {/* Program Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="coach-section">
          <div className="flex items-center">
            <Clock className="w-5 h-5 text-blue-500 mr-2" />
            <div>
              <div className="text-sm text-gray-500">Duration</div>
              <div className="text-lg font-semibold">{currentProgram.duration} weeks</div>
            </div>
          </div>
        </div>
        
        <div className="coach-section">
          <div className="flex items-center">
            <Calendar className="w-5 h-5 text-green-500 mr-2" />
            <div>
              <div className="text-sm text-gray-500">Frequency</div>
              <div className="text-lg font-semibold">{currentProgram.workoutsPerWeek}x per week</div>
            </div>
          </div>
        </div>
        
        <div className="coach-section">
          <div className="flex items-center">
            <Target className="w-5 h-5 text-purple-500 mr-2" />
            <div>
              <div className="text-sm text-gray-500">Focus</div>
              <div className="text-lg font-semibold">
                {currentProgram.focus?.map(f => f.type).join(', ') || 'General'}
              </div>
            </div>
          </div>
        </div>
        
        <div className="coach-section">
          <div className="flex items-center">
            <TrendingUp className="w-5 h-5 text-orange-500 mr-2" />
            <div>
              <div className="text-sm text-gray-500">Progress</div>
              <div className="text-lg font-semibold">
                {currentProgram.progress ? `${Math.round(currentProgram.progress.completionRate)}%` : '0%'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <div className="flex space-x-8">
          {['overview', 'workouts', 'calendar', 'analytics'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab as typeof activeTab)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="space-y-6">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Program Information */}
            <div className="coach-section">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Program Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="text-sm text-gray-500">Difficulty Level</div>
                  <div className="font-medium">{currentProgram.difficulty}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Equipment Required</div>
                  <div className="font-medium">
                    {currentProgram.equipment?.join(', ') || 'None specified'}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Start Date</div>
                  <div className="font-medium">
                    {currentProgram.startDate ? format(new Date(currentProgram.startDate), 'MMM d, yyyy') : 'Not set'}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">End Date</div>
                  <div className="font-medium">
                    {currentProgram.endDate ? format(new Date(currentProgram.endDate), 'MMM d, yyyy') : 'Not set'}
                  </div>
                </div>
              </div>
            </div>

            {/* Week Overview */}
            <div className="coach-section">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Week Overview</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {currentProgram.weeks?.map((week) => (
                  <div key={week.weekNumber} className="p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium">Week {week.weekNumber}</h4>
                      {week.deloadWeek && (
                        <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded">
                          Deload
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-600">
                      {week.workouts.length} workouts
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      ~{formatDuration(week.workouts.reduce((sum, w) => sum + w.estimatedDuration, 0))} total
                    </div>
                  </div>
                )) || (
                  <div className="col-span-full text-center text-gray-500 py-8">
                    No weeks defined yet
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'workouts' && (
          <div className="space-y-6">
            {/* Week Selector */}
            <div className="flex items-center gap-4">
              <label className="text-sm font-medium text-gray-700">Week:</label>
              <select
                value={selectedWeek}
                onChange={(e) => setSelectedWeek(parseInt(e.target.value))}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {Array.from({ length: currentProgram.duration }, (_, i) => (
                  <option key={i + 1} value={i + 1}>
                    Week {i + 1}
                  </option>
                ))}
              </select>
            </div>

            {/* Workouts for Selected Week */}
            {currentProgram.weeks && currentProgram.weeks[selectedWeek - 1] ? (
              <div className="space-y-4">
                {currentProgram.weeks[selectedWeek - 1].workouts.map((workout) => (
                  <div key={workout.id} className="coach-section">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          workout.completed ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'
                        }`}>
                          {workout.completed ? <CheckCircle className="w-5 h-5" /> : <Circle className="w-5 h-5" />}
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900">{workout.name}</h4>
                          <div className="text-sm text-gray-500">
                            Day {workout.day} • {formatDuration(workout.estimatedDuration)} • Difficulty: {workout.difficulty}/10
                          </div>
                        </div>
                      </div>
                      <div className="text-sm text-gray-500">
                        {workout.mainWork?.length || 0} exercises
                      </div>
                    </div>

                    {workout.description && (
                      <p className="text-gray-600 mb-4">{workout.description}</p>
                    )}

                    {/* Exercise List */}
                    <div className="space-y-3">
                      {workout.mainWork?.map((exercise) => (
                        <div key={exercise.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <div className="font-medium text-gray-900">{exercise.name}</div>
                            <div className="text-sm text-gray-600">
                              {exercise.sets?.length || 0} sets
                              {exercise.reps && ` • ${exercise.reps} reps`}
                              {exercise.weight && ` • ${exercise.weight}kg`}
                            </div>
                          </div>
                          <div className="text-sm text-gray-500">
                            {exercise.muscleGroups?.join(', ')}
                          </div>
                        </div>
                      )) || (
                        <div className="text-center text-gray-500 py-4">
                          No exercises defined
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-gray-500 py-8">
                No workouts defined for week {selectedWeek}
              </div>
            )}
          </div>
        )}

        {activeTab === 'calendar' && (
          <ProgramCalendarView program={currentProgram} />
        )}

        {activeTab === 'analytics' && (
          <div className="space-y-6">
            <div className="coach-section">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Program Analytics</h3>
              <div className="text-center text-gray-500 py-8">
                Analytics dashboard coming soon...
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}